import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * A minimal native toggle styled to match the rest of the primitives.
 *
 * Not backed by a Radix primitive — this app only has @radix-ui/react-dialog,
 * -label and -select installed, and pulling in @radix-ui/react-switch just
 * for a two-state toggle isn't worth the new dependency. Follows the same
 * "plain button styled as a control" approach as Checkbox.
 */
export interface SwitchProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked, onCheckedChange, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-muted',
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-5 w-5 translate-x-0.5 rounded-full bg-background shadow-sm transition-transform',
          checked && 'translate-x-5',
        )}
      />
    </button>
  ),
);
Switch.displayName = 'Switch';
